import { useMemo } from 'react';

import settings from '@src/conf';
import { METADATA_OPTIONS_SEED } from '@src/constants/metadataSeed';

import useQuery from '../useQuery';

/**
 * Metadata options reference data (Plane #364). Bounded, user-facing choice sets
 * (`{ <group>: [{ value, label, metadata }] }`) the frontend renders as form options,
 * filters, and chip labels — sourced from `GET /metadata/options/` so the backend
 * TextChoices enums are the single source of truth instead of inlined constants.
 *
 * The endpoint is mounted at the Django SITE ROOT (`/metadata/options/`), not
 * under the versioned `/api/v1` API. The shared origin client's `baseURL` is
 * `${VITE_API_DOMAIN}/api/v1`, and `useBaseQuery` gives no per-request `baseURL`
 * hook, so we pass an ABSOLUTE url (`${site.baseURL}/metadata/options/`); axios
 * uses an absolute url verbatim and ignores `baseURL`, hitting the site root.
 *
 * `initialData` is seeded from `METADATA_OPTIONS_SEED` (derived from the
 * `src/constants/*` modules, aligned to the backend labels) so every consuming
 * `<select>` has its options on FIRST paint — no empty-select flash, no loading
 * state. `staleTime`/`gcTime` are `Infinity`: this is static reference data, so
 * the seeded value is rendered immediately and a single background fetch confirms
 * it; if `initialData` matches the server (it should), nothing visibly changes.
 */
const METADATA_OPTIONS_URL = `${settings.CLIENTS.site.baseURL}/metadata/options/`;

function useOptions(params) {
  const query = useQuery(METADATA_OPTIONS_URL, {
    namespace: 'metadata',
    staleTime: Infinity,
    gcTime: Infinity,
    initialData: METADATA_OPTIONS_SEED,
    ...params,
  });

  // `data` is always defined thanks to `initialData`, so callers never branch on
  // loading. Fall back to the seed defensively in case a caller disables the query.
  return query.data || METADATA_OPTIONS_SEED;
}

/**
 * One option group as `[{ value, label, metadata }]` — the shape a `SelectField`/`Select`
 * `options` prop expects (`metadata` is always a dict, `{}` when empty; consumers that
 * only need value/label may destructure those two). Memoized so the array identity is
 * stable across renders
 * (the underlying object is referentially stable from react-query's cache).
 *
 * MUST be called at component top level (it is a hook) — never inside a
 * conditionally-invoked option-builder helper.
 */
function useOptionGroup(group) {
  const data = useOptions();
  return useMemo(() => data[group] || [], [data, group]);
}

/**
 * One option group as a `{ value: label }` map — for label lookups (chips, table
 * cells, toasts) that key on the stored enum value. MUST be called at component
 * top level.
 */
function useOptionLabels(group) {
  const options = useOptionGroup(group);
  return useMemo(
    () => Object.fromEntries(options.map(({ value, label }) => [value, label])),
    [options],
  );
}

export { useOptionGroup, useOptionLabels, useOptions };
export default useOptions;
